import React, { useState } from 'react';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import 'react-html5-camera-photo/build/css/index.css';

import { UploadPhoto } from '../../../s3Func/s3Upload';
import MediaService from '../../../services/MediaService';

import countdownSound from './count_down_beep.m4a';

import PhotoTimeVideo from './PhotoTimeVideo';
import PhotoTimeCamera from './PhotoTimeCamera';

import loading from './../../../loading-gif-png-5.gif';


/**
 * The PhotoTime part of each module allows the child to take various photos and
 * upload them to the database to be used in later sections such as learn and
 * surprise video
 * @param {*} props
 * @returns The HTML code for phototime
 */
export default function PhotoTime(props) {
  // Initialize variables to be used later
  const { login } = useSelector((state) => state.loginReducer, shallowEqual);
  const dispatch = useDispatch();

  const [waiting, setWaiting] = useState(false);


  const [photoCount, setPhotoCount] = useState(0);
  const [dataUri, setDataUri] = useState('');
  const [countdown, setCountDown] = useState(null);

  const [videoConfig, setVideoConfig] = useState(null);
  const [showVideo, setShowVideo] = useState(false);

  const [cameraConfig, setCameraConfig] = useState(null);
  const [showCamera, setShowCamera] = useState(false);

  const cdSound = new Audio(countdownSound);

  const cameraAudioSound = MediaService.getVideo(
    '/Module1Videos/camera_shutter.m4a',
    login
  );
  const cameraAudio = new Audio(cameraAudioSound);

  // Set a delay timer for ms miliseconds
  function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  // Defines the camera and prepares it to show the correct size and border.
  // Also defines the countdown once the button to take the photo is clicked.
  const handleVideoEnded = (photoType) => {
    console.log(props.moduleNum);
    if (photoType === 'smile') {
      setCameraConfig({ text: 'Smile', photoType });
      if(props.moduleNum === 'MODULE4'){
        setCameraConfig({ text: 'Smile and make the body tall pose!', photoType });
      }
    }

    if (photoType === 'pose') {
      setCameraConfig({ text: 'Show a power pose!', photoType });
      if(props.moduleNum === 'MODULE4'){
        setCameraConfig({ text: 'Make the I can do it all pose!', photoType });
      }
    }

    if (photoType === 'silly') {
      setCameraConfig({ text: `Silly Photo ${photoCount}`, photoType });
    }

    setShowCamera(true);
    setShowVideo(false);
    setVideoConfig(null);
  };

  const handleConfirmPhoto = async () => {
    let fileName = '';
    setWaiting(true);
    if (cameraConfig.photoType === 'smile') {
      if(props.moduleNum === 'MODULE2'){
        fileName = `listening_photo_1.jpg`;
      }
      else{
        fileName = `${props.name}_smile.jpg`;
      }
    
      const result = await UploadPhoto(dataUri, 'userfiles', fileName, 'image/jpeg', login.studentid);

      if (result === 200){
        setPhotoCount(photoCount + 1);
        setWaiting(false);

        if (props.poseVideo) {
          setVideoConfig({ video: props.poseVideo, photoType: 'pose' });
        } else {
          setVideoConfig({ video: props.fourSillyPhotos, photoType: 'silly' });
        }
        setCameraConfig(null);
        setShowVideo(true);
        setShowCamera(false);
      }
      else{
        window.alert('Whoops, failed to upload. It looks like the internet connection was lost. Please try again after login.');
        window.location.reload(false);      
      }

    }

    if (
      cameraConfig.photoType === 'silly' &&
      photoCount > 0 &&
      photoCount < 5
    ) {
      fileName = `${props.name}_silly_photo_${photoCount}.jpg`;
      const result = await UploadPhoto(dataUri, 'userfiles', fileName, 'image/jpeg', login.studentid);

      if (result === 200){
        setWaiting(false);
        setPhotoCount(photoCount + 1);
        setCameraConfig({
          text: `Silly Photo ${photoCount + 1}`,
          photoType: 'silly',
        });
  
        if (photoCount === 4) {
          if (!props.module.reviewIcon) {
            dispatch({
              type: `${props.moduleNum}_SET_stepCompleted`,
              payload: 'phototime',
            });
          }
          props.setDisplayPhotoTime(false);
        }
      }
      else{
        window.alert('Whoops, failed to upload. It looks like the internet connection was lost. Please try again after login.');
        window.location.reload(false);
      }
 
    }

    if (cameraConfig.photoType === 'pose') {
      fileName = `${props.name}_power_pose.jpg`;
      const result = await UploadPhoto(dataUri, 'userfiles', fileName, 'image/jpeg', login.studentid);

      if (result === 200){
        setWaiting(false);
        setVideoConfig({ video: props.fourSillyPhotos, photoType: 'silly' });
        setShowCamera(false);
        setShowVideo(true);
      }
      else{
        window.alert('Whoops, failed to upload. It looks like the internet connection was lost. Please try again after login.');
        window.location.reload(false);
      }

    }

    setDataUri(null);
  };

  const handleCancelPhoto = () => {
    setDataUri(null);
  };

  const handleCameraClick = ({ event, webCamEvent }) => {
    let secondsRemaining = 3;

    const photoInterval = setInterval(async () => {
      event.target.disabled = true;
      setCountDown(secondsRemaining);

      if (secondsRemaining > 0) cdSound.play();

      secondsRemaining--;

      if (secondsRemaining <= -1) {
        cameraAudio.play();
        setCountDown(null);
        clearInterval(photoInterval);
        await sleep(1000);
        const photoUri = webCamEvent.getScreenshot();
        setDataUri(photoUri);
      }
    }, 1000);
  };

  useState(() => {
    // On component start
    setVideoConfig({ video: props.introVideo, photoType: 'smile' });
    setShowVideo(true);
  }, []);

  // The HTML code to use the camera and video creator.
  return (
    <>
      {showVideo && videoConfig && (
        <PhotoTimeVideo
          background={props.background}
          video={videoConfig.video}
          photoType={videoConfig.photoType}
          setDisplayPhotoTime={props.setDisplayPhotoTime}
          onEnded={(val) => handleVideoEnded(val)}
        />
      )}

      {waiting && <div
        className='camera-wrapper'
        style={{ backgroundImage: `url(${props.background})` }}>
          <img alt='loading icon' id='loading' src={loading}/> 
      </div>}

      {showCamera && cameraConfig && !waiting && (
        <PhotoTimeCamera
          background={props.background}
          text={cameraConfig.text}
          dataUri={dataUri}
          countdown={countdown}
          onCameraClick={(event) => handleCameraClick(event)}
          onConfirmPreview={() => handleConfirmPhoto()}
          onCancelPreview={() => handleCancelPhoto()}
        />
      )}
    </>
  );
}
